<template>
    <b-modal
        :visible="show"
        title="Envio de documentos para o SEI"
        size="lg"
        no-close-on-backdrop
        :hide-header-close="processando"
        @hidden="fecharModal"
    >
        <div class="mb-2">
            <strong>Número do processo:</strong> {{ numeroProcesso || '-' }}
        </div>

        <div class="mb-3">
            <strong>Andamento:</strong>
            {{ totalFinalizados }}/{{ totalDocumentos }}
            <b-progress class="mt-2" :max="100" height="18px">
                <b-progress-bar :value="percentualConclusao" variant="info">
                    {{ percentualConclusao }}%
                </b-progress-bar>
            </b-progress>
        </div>

        <div v-if="erroGeral" class="alert alert-danger">
            {{ erroGeral }}
        </div>

        <div class="table-responsive">
            <table class="table table-sm table-bordered">
                <thead>
                    <tr>
                        <th>Documento</th>
                        <th>Tipo</th>
                        <th style="width: 140px;">Status</th>
                        <th>Mensagem</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="itensEnvio.length === 0">
                        <td colspan="4" class="text-center text-muted">
                            Nenhum anexo disponível para envio.
                        </td>
                    </tr>
                    <tr v-for="item in itensEnvio" :key="item.documentoBoletimMedicaoId || item.indice">
                        <td>{{ item.nomeDocumento || item.nomeArquivo || item.nome || '-' }}</td>
                        <td>{{ item.tipoDescricao || item.tipoAnexoBoletimMedicaoDescricao || '-' }}</td>
                        <td>
                            <b-badge :variant="obterBadgeStatus(item.statusEnvio)">
                                {{ item.statusEnvio || '-' }}
                            </b-badge>
                        </td>
                        <td>{{ item.mensagemEnvio || '-' }}</td>
                    </tr>
                </tbody>
            </table>
        </div>

        <template #modal-footer>
            <div class="w-100 d-flex justify-content-between align-items-center">
                <small v-if="processando" class="text-muted">
                    Enviando documentos, aguarde...
                </small>
                <small v-else-if="totalErros > 0" class="text-danger">
                    Finalizado com {{ totalErros }} erro(s).
                </small>
                <small v-else-if="finalizado" class="text-success">
                    Todos os documentos foram enviados.
                </small>
                <small v-else class="text-muted"></small>

                <b-button variant="secondary" :disabled="processando" @click="fecharModal">
                    Fechar
                </b-button>
            </div>
        </template>
    </b-modal>
</template>

<script>
import IntegracaoSeiDocumentosServicos from '@/shared/integracoes/integracaoSeiDocumentosServicos.js'
import {
    enviarDocumentosSeiLoteComRetry,
    statusEnvioDocumentoSeiEnum
} from '@/shared/integracoes/envioDocumentosSeiLote.js'

export default {
    props: {
        show: {
            type: Boolean,
            default: false
        },
        contratoId: {
            type: [String, Number],
            default: null
        },
        boletimMedicaoId: {
            type: [String, Number],
            default: null
        },
        numeroProcesso: {
            type: String,
            default: ''
        },
        anexos: {
            type: Array,
            default: () => []
        },
        parametros: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            itensEnvio: [],
            processando: false,
            finalizado: false,
            erroGeral: null,
            jaIniciado: false
        }
    },

    computed: {
        totalDocumentos() {
            return this.itensEnvio.length
        },
        totalEnviados() {
            return this.itensEnvio.filter(x => x.statusEnvio === statusEnvioDocumentoSeiEnum.ENVIADO).length
        },
        totalErros() {
            return this.itensEnvio.filter(x => x.statusEnvio === statusEnvioDocumentoSeiEnum.ERRO).length
        },
        totalFinalizados() {
            return this.totalEnviados + this.totalErros
        },
        percentualConclusao() {
            if (!this.totalDocumentos) return 0
            return Math.round((this.totalFinalizados / this.totalDocumentos) * 100)
        }
    },

    watch: {
        show(novoValor) {
            if (!novoValor) {
                this.resetarEstado()
                return
            }

            if (this.jaIniciado) return

            this.jaIniciado = true
            this.iniciarFluxoEnvio()
        }
    },

    methods: {
        resetarEstado() {
            this.itensEnvio = []
            this.processando = false
            this.finalizado = false
            this.erroGeral = null
            this.jaIniciado = false
        },

        obterBadgeStatus(status) {
            switch (status) {
                case statusEnvioDocumentoSeiEnum.ENVIADO:
                    return 'success'
                case statusEnvioDocumentoSeiEnum.ERRO:
                    return 'danger'
                default:
                    return 'secondary'
            }
        },

        async enviarDocumentoSei({ anexo, parametros }) {
            const payload = {
                ...parametros,
                numeroProcesso: this.numeroProcesso,
                contratoId: this.contratoId,
                boletimMedicaoId: this.boletimMedicaoId,
                documentoBoletimMedicaoId: anexo.documentoBoletimMedicaoId,
                nomeDocumento: anexo.nomeDocumento || anexo.nomeArquivo || anexo.nome
            }

            return IntegracaoSeiDocumentosServicos.enviarDocumentoIndividualParaSei(
                this.contratoId,
                this.boletimMedicaoId,
                payload
            )
        },

        async iniciarFluxoEnvio() {
            this.processando = true
            this.finalizado = false
            this.erroGeral = null

            const anexos = (this.anexos || []).filter(item => !!item.documentoBoletimMedicaoId)
            this.itensEnvio = anexos.map((item, indice) => ({
                ...item,
                indice,
                statusEnvio: '',
                mensagemEnvio: ''
            }))

            if (anexos.length === 0) {
                this.processando = false
                this.finalizado = true
                this.$emit('concluido', {
                    sucesso: true,
                    total: 0,
                    enviados: 0,
                    erros: 0,
                    itens: []
                })
                return
            }

            try {
                const resultado = await enviarDocumentosSeiLoteComRetry({
                    anexos,
                    parametros: this.parametros,
                    maxTentativas: 2,
                    enviarDocumento: this.enviarDocumentoSei,
                    onAtualizacao: itensAtualizados => {
                        this.itensEnvio = itensAtualizados
                    }
                })

                this.itensEnvio = resultado.itens
                this.$emit('concluido', resultado)
            } catch (erro) {
                this.erroGeral = erro.message || 'Erro geral no envio dos documentos'
                this.$emit('concluido', {
                    sucesso: false,
                    total: this.totalDocumentos,
                    enviados: this.totalEnviados,
                    erros: this.totalErros,
                    itens: this.itensEnvio
                })
            } finally {
                this.processando = false
                this.finalizado = true
            }
        },

        fecharModal() {
            if (this.processando) return
            this.$emit('fechar')
        }
    }
}
</script>
